import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import type { CreditBucket } from '../../hooks/credits/useCreditBalance'

type Variant = 'rows' | 'chips'

interface BalanceSourceBreakdownProps {
  buckets: CreditBucket[]
  variant: Variant
  /** Dark hero chips on /credits */
  chipsTone?: 'dark' | 'light'
  emptyLabel?: string
  className?: string
}

function sourceLabel(source: string, hasSubscription: boolean, t: TFunction<'credits'>): string {
  if (source === 'free_plan' && hasSubscription) {
    return t('sources.residual')
  }
  return t(`sources.${source}`, { defaultValue: source })
}

/**
 * Provenance-only balance slices. Expiry belongs in {@link ExpiringNextNotice}.
 */
export function BalanceSourceBreakdown({
  buckets,
  variant,
  chipsTone = 'dark',
  emptyLabel,
  className = '',
}: BalanceSourceBreakdownProps) {
  const { t } = useTranslation('credits')
  const hasSubscription = buckets.some((b) => b.source === 'subscription')

  if (buckets.length === 0) {
    if (!emptyLabel) return null
    return <div className={`text-sm text-gray-400 ${className}`}>{emptyLabel}</div>
  }

  if (variant === 'chips') {
    const chipClass =
      chipsTone === 'dark'
        ? 'bg-white/20 text-white'
        : 'bg-gray-100 text-gray-800'
    const residualClass =
      chipsTone === 'dark' ? 'bg-white/10 opacity-70 text-white' : 'bg-gray-50 text-gray-500'

    return (
      <div className={`flex flex-wrap gap-2 ${className}`}>
        {buckets.map((bucket) => {
          const isResidual = bucket.source === 'free_plan' && hasSubscription
          return (
            <span
              key={bucket.source}
              className={`text-xs rounded-full px-3 py-1 ${isResidual ? residualClass : chipClass}`}
            >
              {sourceLabel(bucket.source, hasSubscription, t)}:{' '}
              {bucket.total.toLocaleString()} {t('unit')}
            </span>
          )
        })}
      </div>
    )
  }

  return (
    <div className={className}>
      {buckets.map((bucket) => (
        <div key={bucket.source} className="flex justify-between text-sm py-0.5">
          <span className="text-gray-600">
            {sourceLabel(bucket.source, hasSubscription, t)}
          </span>
          <span className="font-medium">
            {bucket.total.toLocaleString()} {t('unit')}
          </span>
        </div>
      ))}
    </div>
  )
}
